import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, TextField, MenuItem, Tooltip as MuiTooltip } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../../api';

const localizer = momentLocalizer(moment);

const MeetingDashboard = ({ viewType }) => {
  const [meetings, setMeetings] = useState([]);
  const [masters, setMasters] = useState({ purposes: [], depts: [] });
  const [loading, setLoading] = useState(true);

  // Filters defaulting to Current Month
  const [filters, setFilters] = useState({
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    department: '',
    purpose: ''
  });

  const [calendarView, setCalendarView] = useState(Views.DAY);
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = viewType === 'ALL' ? '/meeting-views/all-meetings' : '/meeting-views/my-meetings';
        const [meetRes, purpRes, deptRes] = await Promise.all([
          api.get(endpoint),
          api.get('/meeting-views/purposes'),
          api.get('/meeting-views/departments')
        ]);
        setMeetings(meetRes.data);
        setMasters({ purposes: purpRes.data, depts: deptRes.data });
      } catch (err) {
        console.error("Dashboard data fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [viewType]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // Filter Data
  const filteredMeetings = meetings.filter(m => {
    const mDate = moment(m.meeting_date);
    const inDateRange = mDate.isSameOrAfter(filters.startDate) && mDate.isSameOrBefore(filters.endDate);
    const inDept = filters.department ? m.department_id === filters.department : true;
    const inPurpose = filters.purpose ? m.purpose_id === filters.purpose : true;
    return inDateRange && inDept && inPurpose;
  });

  // KPI Calculations
  const completed = filteredMeetings.filter(m => m.status === 'COMPLETED');
  const upcoming = filteredMeetings.filter(m => m.status === 'SCHEDULED');

  const totalHours = filteredMeetings
    .filter(m => m.status !== 'CANCELLED')
    .reduce((acc, curr) => acc + (curr.duration_minutes / 60), 0);

  const totalParticipants = completed.reduce((acc, curr) => acc + (curr.attendees ? curr.attendees.length : 0), 0);

  // Generate Continuous Date Array for the Line Chart based on Filter Range
  const generateChartData = () => {
    const start = moment(filters.startDate);
    const end = moment(filters.endDate);
    const dataMap = {};

    for (let m = moment(start); m.isSameOrBefore(end); m.add(1, 'days')) {
      dataMap[m.format('YYYY-MM-DD')] = { date: m.format('DD MMM'), Completed: 0, Upcoming: 0 };
    }

    filteredMeetings.forEach(m => {
      if (m.status === 'CANCELLED') return;
      const rawDate = m.meeting_date;
      if (dataMap[rawDate]) {
        if (m.status === 'COMPLETED') dataMap[rawDate].Completed += 1;
        if (m.status === 'SCHEDULED') dataMap[rawDate].Upcoming += 1;
      }
    });

    return Object.values(dataMap);
  };

  const chartData = generateChartData();

  // Calendar Events Prep
  const calendarEvents = filteredMeetings.filter(m => m.status !== 'CANCELLED').map(m => {
    return {
      id: m.id,
      title: m.subject,
      start: new Date(`${m.meeting_date}T${m.start_time}`),
      end: new Date(`${m.meeting_date}T${m.end_time}`),
      status: m.status,
      duration: m.duration_minutes,
      purposeName: masters.purposes.find(p => p.id === m.purpose_id)?.purpose_name || ''
    };
  });

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.status === 'COMPLETED' ? '#4caf50' : '#1976d2',
        borderRadius: '4px', opacity: 0.9, color: 'white', border: '0px', display: 'block'
      }
    };
  };

  const CustomEvent = ({ event }) => (
    <MuiTooltip title={`${event.title} (${event.status}) - ${event.purposeName} | ${event.duration} Mins`} placement="top" arrow>
      <Box sx={{ fontSize: '0.75rem', p: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {event.title}
      </Box>
    </MuiTooltip>
  );

  if (loading) return <CircularProgress sx={{ display: 'block', m: 'auto', mt: 5 }} />;

  return (
    <Box>
      {/* FILTER ROW */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" type="date" label="From Date" name="startDate" value={filters.startDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" type="date" label="To Date" name="endDate" value={filters.endDate} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              select
              label="Department"
              name="department"
              value={filters.department}
              onChange={handleFilterChange}
              sx={{ width: { xs: '100%', md: 220 } }}
            >
              <MenuItem value=""><em>All Departments</em></MenuItem>
              {masters.depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              select
              label="Purpose"
              name="purpose"
              value={filters.purpose}
              onChange={handleFilterChange}
              sx={{ width: { xs: '100%', md: 220 } }}
            >
              <MenuItem value=""><em>All Purposes</em></MenuItem>
              {masters.purposes.map(p => <MenuItem key={p.id} value={p.id}>{p.purpose_name}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* KPI CARDS */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderTop: '4px solid #4caf50' }}>
            <Typography color="textSecondary" variant="subtitle2">Completed Meetings</Typography>
            <Typography variant="h4" color="success.main">{completed.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderTop: '4px solid #1976d2' }}>
            <Typography color="textSecondary" variant="subtitle2">Upcoming Meetings</Typography>
            <Typography variant="h4" color="primary.main">{upcoming.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderTop: '4px solid #9c27b0' }}>
            <Typography color="textSecondary" variant="subtitle2">Total Participants</Typography>
            <Typography variant="h4" color="secondary">{totalParticipants}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, textAlign: 'center', borderTop: '4px solid #ff9800' }}>
            <Typography color="textSecondary" variant="subtitle2">Meeting Hours</Typography>
            <Typography variant="h4" color="warning.main">{totalHours.toFixed(1)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ============================================================
          CHARTS AND CALENDAR — CSS Grid guarantees 2fr / 1fr split
          ============================================================ */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, // 2/3 : 1/3
          gap: 3,
          width: '100%',
          mb: 2
        }}
      >
{/* LINE CHART — 2/3 Width */}
<Paper
  elevation={2}
  sx={{
    width: '100%',
    minWidth: 0,
    height: 440,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  }}
>
  <Typography variant="h6" gutterBottom color="primary">
    Meeting Trend
  </Typography>
  {chartData.length > 0 ? (
    <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          {/* Gradient definitions for a stylish look */}
          <defs>
            <linearGradient id="completedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="upcomingGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e0e0e0" />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={{ stroke: '#ccc' }}
            interval={Math.max(0, Math.ceil(chartData.length / 4) - 1)}  // show ~4 labels
            angle={0}
            textAnchor="middle"
            height={30}
          />

          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={false}
            domain={[0, 'dataMax + 1']}
            width={30}
          />

          <RechartsTooltip
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              fontSize: '0.8rem'
            }}
            labelStyle={{ fontWeight: 600, color: '#333' }}
            cursor={{ stroke: '#bdbdbd', strokeDasharray: '3 3' }}
          />

          <Legend
            verticalAlign="top"
            height={28}
            iconType="circle"
            wrapperStyle={{ fontSize: '0.8rem' }}
          />

          <Line
            type="monotone"
            name="Completed"
            dataKey="Completed"
            stroke="#4caf50"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
          />
          <Line
            type="monotone"
            name="Upcoming"
            dataKey="Upcoming"
            stroke="#1976d2"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: '#fff' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  ) : (
    <Typography color="textSecondary" sx={{ mt: 8, textAlign: 'center' }}>
      No meeting data available.
    </Typography>
  )}
</Paper>
        {/* CALENDAR — 1/3 Width */}
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            minWidth: 0,
            height: 440,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Typography variant="h6" gutterBottom color="primary">
            Calendar
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: 'hidden',
              mt: 1,
              // Force react-big-calendar to shrink below its default min-width
              '& .rbc-calendar': { width: '100%', minWidth: 0 },
              '& .rbc-time-view': { minWidth: 0 },
              '& .rbc-time-header': { minWidth: 0 },
              '& .rbc-time-content': { minWidth: 0 },
              '& .rbc-time-header-content': { minWidth: 0 },
              '& .rbc-toolbar': {
                fontSize: '0.7rem',
                mb: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px'
              },
              '& .rbc-toolbar button': { fontSize: '0.7rem', padding: '3px 6px' },
              '& .rbc-event': { padding: 0, fontSize: '0.7rem' },
              '& .rbc-header': { fontSize: '0.7rem', padding: '2px' },
              '& .rbc-time-gutter, & .rbc-label': { fontSize: '0.7rem' }
            }}
          >
<Calendar
  localizer={localizer}
  events={calendarEvents}
  startAccessor="start"
  endAccessor="end"
  style={{ height: '100%' }}
  eventPropGetter={eventStyleGetter}
  components={{ event: CustomEvent }}
  views={['month', 'week', 'day']}
  view={calendarView}
  onView={(newView) => setCalendarView(newView)}
  date={calendarDate}
  onNavigate={(newDate) => setCalendarDate(newDate)}
  step={30}
  timeslots={2}
  scrollToTime={new Date(1970, 0, 1, 10, 0, 0)}   // ← NEW: auto-scroll to 10:00 AM
  showMultiDayTimes
  popup
/>          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MeetingDashboard;